import {
  Color,
  GeoJsonDataSource,
  ScreenSpaceEventHandler,
  defined,
  ScreenSpaceEventType,
  HeightReference,
  JulianDate,
  ColorMaterialProperty,
  Cartesian2,
  BoundingSphere,
  LabelGraphics,
  LabelStyle,
  PolylineDashMaterialProperty,
} from "cesium";
import { createUniqueId } from "./Utility";

export const convertOSMToGeojsonForLaneletView = (xmlDoc) => {
  const geojson = {
    type: "FeatureCollection",
    properties: {
      type: "OSM Lanelet",
      version: "1.0",
    },
    features: [],
  };

  const nodes = {};
  xmlDoc.querySelectorAll("node").forEach((node) => {
    const id = node.getAttribute("id");
    const lat = parseFloat(node.getAttribute("lat"));
    const lon = parseFloat(node.getAttribute("lon"));
    let ele = 0;
    node.querySelectorAll("tag").forEach((tag) => {
      if (tag.getAttribute("k") === "ele") {
        ele = parseFloat(tag.getAttribute("v"));
      }
    });
    nodes[id] = [lon, lat, ele];
  });

  const ways = {};
  xmlDoc.querySelectorAll("way").forEach((way) => {
    const id = way.getAttribute("id");
    const nds = Array.from(way.querySelectorAll("nd")).map((nd) => nd.getAttribute("ref"));
    ways[id] = nds;
  });

  const relations = xmlDoc.querySelectorAll("relation");

  relations.forEach((rel) => {
    const tags = rel.querySelectorAll("tag");
    const memberElements = rel.querySelectorAll("member");

    const leftWayNodeRefs = [];
    const rightWayNodeRefs = [];

    memberElements.forEach((member) => {
      const type = member.getAttribute("type");
      const ref = member.getAttribute("ref");
      const role = member.getAttribute("role");

      if (type === "way" && role === "left") {
        leftWayNodeRefs.push(...(ways[ref] || []));
      } else if (type === "way" && role === "right") {
        rightWayNodeRefs.push(...(ways[ref] || []));
      }
    });

    const cornerTag = Array.from(tags).find((tag) => tag.getAttribute("k") === "corner_pt_ids");

    const properties = {
      relation: {},
      way: [],
      node: [],
    };

    properties.relation["id"] = rel.getAttribute("id");

    for (let attr of rel.attributes) {
      if (attr.name !== "id") {
        properties.relation[attr.name] = attr.value;
      }
    }

    tags.forEach((tag) => {
      const k = tag.getAttribute("k");
      const v = tag.getAttribute("v");

      if (k === "id") {
        properties.relation["id2"] = v;
      } else {
        properties.relation[k] = v;
      }
    });

    // Way tags
    const allWayRefs = new Set();
    memberElements.forEach((member) => {
      if (member.getAttribute("type") === "way") {
        allWayRefs.add(member.getAttribute("ref"));
      }
    });

    allWayRefs.forEach((wayId) => {
      const wayEl = xmlDoc.querySelector(`way[id="${wayId}"]`);
      if (wayEl) {
        const wayTags = {};
        wayEl.querySelectorAll("tag").forEach((tag) => {
          wayTags[tag.getAttribute("k")] = tag.getAttribute("v");
        });
        properties.way.push({ id: wayId, tags: wayTags });
      }
    });

    if (cornerTag) {
      const cornerPtIds = cornerTag
        .getAttribute("v")
        .split(",")
        .map((id) => id.trim());

      if (cornerPtIds.length !== 4) return;

      const [pt1, pt2, pt3, pt4] = cornerPtIds;

      if (leftWayNodeRefs.length > 0 && leftWayNodeRefs[leftWayNodeRefs.length - 1] !== pt1) {
        leftWayNodeRefs.reverse();
      }
      if (rightWayNodeRefs.length > 0 && rightWayNodeRefs[0] !== pt2) {
        rightWayNodeRefs.reverse();
      }

      const coords = [];

      leftWayNodeRefs.forEach((id) => {
        if (nodes[id]) coords.push(nodes[id]);
      });
      if (nodes[pt1]) coords.push(nodes[pt1]);
      if (nodes[pt2]) coords.push(nodes[pt2]);
      rightWayNodeRefs.forEach((id) => {
        if (nodes[id]) coords.push(nodes[id]);
      });
      if (nodes[pt3]) coords.push(nodes[pt3]);
      if (nodes[pt4]) coords.push(nodes[pt4]);

      if (coords.length > 0 && coords[0].toString() !== coords[coords.length - 1].toString()) {
        coords.push(coords[0]);
      }

      // Node tags
      const usedNodeIds = new Set([...leftWayNodeRefs, ...rightWayNodeRefs, pt1, pt2, pt3, pt4]);
      usedNodeIds.forEach((nodeId) => {
        const nodeEl = xmlDoc.querySelector(`node[id="${nodeId}"]`);
        if (nodeEl) {
          const nodeTags = {};
          nodeEl.querySelectorAll("tag").forEach((tag) => {
            nodeTags[tag.getAttribute("k")] = tag.getAttribute("v");
          });
          properties.node.push({ id: nodeId, tags: nodeTags });
        }
      });

      geojson.features.push({
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [coords],
        },
        properties,
      });
    } else {
      const coordsList = [];

      allWayRefs.forEach((wayId) => {
        const ndRefs = ways[wayId];
        if (ndRefs) {
          const coords = ndRefs.map((id) => nodes[id]).filter(Boolean);
          if (coords.length > 1) {
            coordsList.push(coords);
          }
        }
      });

      if (coordsList.length === 1) {
        geojson.features.push({
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: coordsList[0],
          },
          properties,
        });
      } else if (coordsList.length > 1) {
        geojson.features.push({
          type: "Feature",
          geometry: {
            type: "MultiLineString",
            coordinates: coordsList,
          },
          properties,
        });
      }
    }
  });

  return geojson;
};

export const deduplicateLaneletFeaturesById = (geojsonIn) => {
  if (!geojsonIn || geojsonIn.type !== "FeatureCollection") return geojsonIn;

  // hold the first occurrence of every unique id we encounter
  const seenIds = new Set();
  const dedupedFeatures = [];

  geojsonIn.features.forEach((feat) => {
    const id = feat?.properties?.id;

    // skip the feature if the id already exists
    if (id !== undefined && id !== null) {
      if (seenIds.has(id)) return; // duplicate → ignore
      seenIds.add(id); // first time → remember
    }

    dedupedFeatures.push(feat); // keep the feature
  });

  // return a shallow-copied FeatureCollection with filtered features
  return {
    ...geojsonIn,
    features: dedupedFeatures,
  };
};

const waysMapCache = new WeakMap();
export const setupOSMLineClickHandler = (viewer) => {
  if (!viewer || !viewer.scene) return;

  // Prevent multiple handlers — only set up once
  if (viewer.__osmLineClickHandler) return;

  let selectedEntity = null;
  const changedEntities = new Set();

  const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);
  viewer.__osmLineClickHandler = handler;

  handler.setInputAction((click) => {
    const picked = viewer.scene.pick(click.position);

    // If nothing is picked
    if (!defined(picked) || !picked.id) {
      if (selectedEntity) {
        resetWayColorsForOSMGeojson(changedEntities);
        changedEntities.clear();
      }
      selectedEntity = null;
      return;
    }

    const entity = picked.id;

    // Find owning dataSource
    let ownerDataSource = null;
    for (const ds of viewer.dataSources._dataSources) {
      if (ds.entities.contains(entity)) {
        ownerDataSource = ds;
        break;
      }
    }

    if (ownerDataSource?.layerType !== "OSM" || ownerDataSource?.layerView !== "line") {
      return;
    }

    // Reset previous highlights
    if (selectedEntity) {
      resetWayColorsForOSMGeojson(changedEntities);
      changedEntities.clear();
    }

    selectedEntity = entity;

    // Get or build waysMap for this dataSource
    let waysMap = waysMapCache.get(ownerDataSource);

    if (!waysMap) {
      waysMap = new Map();
      const all = ownerDataSource.entities.values;

      for (let i = 0; i < all.length; i++) {
        const e = all[i];
        const wayId = e?.properties?.WayId?.getValue();
        const relations = e?.properties?.Relations?.getValue();

        if (wayId && relations) {
          waysMap.set(wayId, { entity: e, relations });
        }
      }

      waysMapCache.set(ownerDataSource, waysMap);
    }

    // Highlight related ways
    if (entity?.properties?.Relations) {
      const relations = entity.properties.Relations?.getValue();
      highlightRelatedWaysForLineViw(relations, waysMap, changedEntities, entity);
      viewer.selectedEntity = entity;
    }
  }, ScreenSpaceEventType.LEFT_CLICK);
};

export const addOSMGeojsonToMapForLaneletViewChunked = async (viewer, geoJson, options = {}, callback = null, layerName = "") => {
  const settings = {
    chunkSize: 3000,
    defaultOutlineColor: Color.GRAY.withAlpha(0.6),
    highlightColor: {
      main: Color.RED.withAlpha(0.8),
      preceding: Color.GREEN.withAlpha(0.8),
      following: Color.PURPLE.withAlpha(0.8),
      reset: Color.GRAY.withAlpha(0.6),
    },
    ...options,
  };

  const data = typeof geoJson === "string" ? JSON.parse(geoJson) : geoJson;
  const dataSource = new GeoJsonDataSource(createUniqueId());
  dataSource.layerName = layerName;
  dataSource.layerType = "OSM";
  dataSource.layerView = "lanelet";
  await viewer.dataSources.add(dataSource);

  const features = data.features;
  const idToEntityMap = {};
  let allEntities = [];
  let lastHighlighted = { main: [], preceding: [], following: [] };

  function resetHighlight() {
    [...lastHighlighted.main, ...lastHighlighted.preceding, ...lastHighlighted.following].forEach((ent) => {
      if (ent.polygon) {
        ent.polygon.material = ent.polygon._secondaryColor || settings.defaultOutlineColor;
      }
    });
    lastHighlighted = { main: [], preceding: [], following: [] };
  }

  const chunkFeatures = async (start) => {
    const end = Math.min(start + settings.chunkSize, features.length);
    const chunk = { ...data, features: features.slice(start, end) };

    const tempDataSource = await GeoJsonDataSource.load(chunk);
    const entities = tempDataSource.entities.values;

    entities.forEach((entity) => {
      if (entity.polygon) {
        // Extract type and subtype from relation
        let relationProps = entity.properties?.relation;
        relationProps = typeof relationProps?.getValue === "function" ? relationProps.getValue() : relationProps || {};

        const type = relationProps.type;
        const subtype = relationProps.subtype;

        // Determine style based on type > subtype > default
        let styleDef = null;
        if (type && settings[type]) {
          styleDef = settings[type];
        } else if (subtype && settings[subtype]) {
          styleDef = settings[subtype];
        }

        const outlineColor = styleDef?.defaultOutlineColor ? Color.fromCssColorString(styleDef.defaultOutlineColor) : settings.defaultOutlineColor;
        const fillColor = styleDef?.defaultFillColor ? Color.fromCssColorString(styleDef.defaultFillColor) : settings.defaultFillColor;

        entity.polygon.material = fillColor;
        entity.polygon.outline = true;
        entity.polygon.outlineColor = outlineColor;

        // if (!entity.polygon._originalMaterial) {
        entity.polygon._originalMaterial = entity.polygon.material;
        entity.polygon._originalOutlineColor = entity.polygon.outlineColor;
        // }

        // if (!entity.polygon._secondaryColor) {
        entity.polygon._secondaryColor = settings.highlightColor.reset;
        // }
      }

      // Register entity by relation id (if available)
      let id;
      try {
        const relation = entity.properties?.relation;
        const resolved = typeof relation?.getValue === "function" ? relation.getValue() : relation;
        id = resolved?.id;
      } catch (e) {
        id = undefined;
      }

      if (id) {
        idToEntityMap[id] = entity;
      }

      dataSource.entities.add(entity);
    });

    if (typeof callback === "function") {
      entities.forEach((entity, index) => {
        const isNext = Math.min(end + settings.chunkSize, features.length) - end >= settings.chunkSize;
        callback(entity, start + index, dataSource, data, isNext, { start, end, total: features.length });
      });
    }

    allEntities = allEntities.concat(entities);

    if (end < features.length) {
      await new Promise((resolve) => setTimeout(resolve, 0));
      return chunkFeatures(end);
    } else {
      if (allEntities.length > 0 && settings.flyToLocation !== false) {
        viewer.zoomTo(dataSource);
      }

      return dataSource;
    }
  };

  return chunkFeatures(0).catch(console.error);
};

const laneletEntityCache = new WeakMap();
export const setupOSMLaneletClickHandler = (viewer) => {
  if (!viewer || !viewer.scene) return;

  if (viewer.__osmLaneletClickHandler) return;

  viewer.__osmLaneletClickHandler = new ScreenSpaceEventHandler(viewer.scene.canvas);

  let lastHighlighted = { main: [], preceding: [], following: [] };

  function resetHighlight() {
    [...lastHighlighted.main, ...lastHighlighted.preceding, ...lastHighlighted.following].forEach((ent) => {
      if (ent.polygon && ent.polygon._originalMaterial) {
        ent.polygon.material = ent.polygon._originalMaterial;
        ent.polygon.outlineColor = ent.polygon._originalOutlineColor;
      } else if (ent.polygon) {
        ent.polygon.material = Color.GRAY.withAlpha(0.6);
      }
    });
    lastHighlighted = { main: [], preceding: [], following: [] };
  }

  viewer.__osmLaneletClickHandler.setInputAction((movement) => {
    const picked = viewer.scene.pick(movement.position);

    if (!picked?.id?.polygon) {
      resetHighlight();
      return;
    }

    const entity = picked.id;

    let ownerDataSource = null;
    for (const ds of viewer.dataSources._dataSources) {
      if (ds.entities.contains(entity)) {
        ownerDataSource = ds;
        break;
      }
    }

    if (ownerDataSource?.layerType !== "OSM" || ownerDataSource?.layerView !== "lanelet") return;

    resetHighlight();

    entity.polygon.material = Color.RED.withAlpha(0.8);
    lastHighlighted.main.push(entity);

    const relationProps = entity.properties?.relation?.getValue?.() ?? entity.properties?.relation ?? {};
    const precedingRaw = relationProps.preceding_llt_ids;
    const followingRaw = relationProps.following_llt_ids;

    const parseIds = (raw) =>
      raw
        ?.split(",")
        .map((id) => id.trim())
        .filter(Boolean) ?? [];

    const precedingIds = parseIds(precedingRaw);
    const followingIds = parseIds(followingRaw);

    // ❗ Build cache of relation.id → entity (per dataSource)
    let relMap = laneletEntityCache.get(ownerDataSource);
    if (!relMap) {
      relMap = new Map();
      for (const e of ownerDataSource.entities.values) {
        const rel = e.properties?.relation?.getValue?.() ?? e.properties?.relation;
        if (rel?.id) relMap.set(rel.id, e);
      }
      laneletEntityCache.set(ownerDataSource, relMap);
    }

    const precedingEntities = precedingIds.map((id) => relMap.get(id)).filter(Boolean);
    const followingEntities = followingIds.map((id) => relMap.get(id)).filter(Boolean);

    precedingEntities.forEach((e) => {
      if (e.polygon) e.polygon.material = Color.GREEN.withAlpha(0.8);
    });

    followingEntities.forEach((e) => {
      if (e.polygon) e.polygon.material = Color.PURPLE.withAlpha(0.8);
    });

    lastHighlighted.preceding.push(...precedingEntities);
    lastHighlighted.following.push(...followingEntities);
  }, ScreenSpaceEventType.LEFT_CLICK);
};

export const highlightRelatedWaysForLineViw = (relations, waysMap, changedEntities, currentEntity) => {
  if (!waysMap || waysMap.size === 0) return;

  const findPreviousAndNextWays = (currentWay, waysMap, nodeType) => {
    if (!currentWay || !currentWay.properties || !currentWay.polyline) return [];

    const nodeId = nodeType === "previous" ? currentWay.properties.nodes?._value?.[0]?.id : currentWay.properties.nodes?._value?.at(-1)?.id;

    if (!nodeId) return [];

    const matchedWays = [];
    waysMap.forEach((way) => {
      const nodeToMatch = nodeType === "previous" ? way.entity.properties.nodes?._value?.at(-1)?.id : way.entity.properties.nodes?._value?.[0]?.id;

      if (nodeToMatch === nodeId) matchedWays.push(way);
    });

    return matchedWays;
  };

  resetWayColorsForOSMGeojson(changedEntities);

  const applyHighlight = (entity, color, width) => {
    if (!entity.polyline) return;

    if (!entity.originalColor) {
      const tags = entity.properties?.Tags?.getValue() || {};
      entity.originalSubtype = tags?.subtype;
      entity.originalColor = entity.polyline.material?.color?.getValue?.() || Color.WHITE;
      entity.originalWidth = entity.polyline.width?.getValue?.() || 2;
    }

    entity.polyline.material = new ColorMaterialProperty(color);
    entity.polyline.width = width;
    changedEntities.add(entity);
  };

  waysMap.forEach((way) => {
    const wayRelations = way.relations;
    if (!wayRelations?.length || !relations?.length) return;

    const intersectionExists = relations.some((rel) => wayRelations.some((wRel) => rel.id === wRel.id));

    if (intersectionExists) {
      applyHighlight(way.entity, Color.BLUE, 4);

      findPreviousAndNextWays(way.entity, waysMap, "previous").forEach((w) => applyHighlight(w.entity, Color.GREEN, 4));
      findPreviousAndNextWays(way.entity, waysMap, "next").forEach((w) => applyHighlight(w.entity, Color.PURPLE, 4));
    }
  });

  if (currentEntity && currentEntity.polyline) {
    applyHighlight(currentEntity, Color.RED, 5);
  }
};

export const toggleRightLaneHighlight = (dataSource, highlight = Color.PURPLE.withAlpha(0.8)) => {
  if (!dataSource) return;

  const toggled = !dataSource._rightLaneToggled;
  dataSource._rightLaneToggled = toggled;

  dataSource.entities.values.forEach((entity) => {
    let rightLane = false;

    const relationProp = entity.properties?.relation;
    const relation = typeof relationProp?.getValue === "function" ? relationProp.getValue() : relationProp;

    if (relation && typeof relation === "object" && "right_lane" in relation) {
      const rlValue = relation.right_lane;
      const rl = typeof rlValue?.getValue === "function" ? rlValue.getValue() : rlValue;

      rightLane = String(rl).trim().toLowerCase() === "true";
    }

    if (!rightLane) return;

    // === Polygon ===
    if (entity.polygon) {
      if (toggled) {
        if (!entity.polygon._originalMaterial) {
          entity.polygon._originalMaterial = entity.polygon.material;
        }
        entity.polygon._secondaryColor = highlight;
        entity.polygon.material = highlight;
      } else {
        if (entity.polygon._originalMaterial) {
          entity.polygon.material = entity.polygon._originalMaterial;
        }
        entity.polygon._secondaryColor = Color.GRAY.withAlpha(0.6);
      }
    }

    // === Polyline ===
    if (entity.polyline) {
      if (toggled) {
        if (!entity.polyline._originalMaterial) {
          entity.polyline._originalMaterial = entity.polyline.material;
        }
        entity.polyline._secondaryColor = highlight;
        entity.polyline.material = highlight;
      } else {
        if (entity.polyline._originalMaterial) {
          entity.polyline.material = entity.polyline._originalMaterial;
        }
        entity.polyline._secondaryColor = Color.GRAY.withAlpha(0.6);
      }
    }
  });
};

export const resetWayColorsForOSMGeojson = (changedEntities) => {
  // If there are no changed entities, exit the function early.
  if (!changedEntities.size) return;

  // Iterate over each modified entity.
  changedEntities.forEach((entity) => {
    const polyline = entity.polyline;
    if (!polyline) return;

    // Restore original properties or apply default values if they don't exist.
    const color = entity.originalColor || Color.YELLOW;
    const width = entity.originalWidth || 2;
    const subtype = entity.originalSubtype || "solid";

    // Apply the appropriate material based on the subtype.
    if (subtype === "dashed") {
      polyline.material = new PolylineDashMaterialProperty({
        color: color,
        dashLength: 16.0,
      });
    } else {
      polyline.material = new ColorMaterialProperty(color);
    }

    // Restore the original width of the polyline.
    polyline.width = width;
  });

  // Clear the set of changed entities since they've been reset.
  changedEntities.clear();
};

export const applyOSMStyleToEntitiesByType = (viewer, styleTypes = {}) => {
  if (!viewer || !viewer.dataSources) return;

  viewer.dataSources._dataSources.forEach((ds) => {
    if (ds.layerType !== "OSM") return;

    const isLanelet = ds.layerView === "lanelet";
    const isLine = ds.layerView === "line";

    ds.entities.values.forEach((entity) => {
      let type = null;
      let subtype = null;

      // Extract type/subtype based on view type
      if (isLine && entity.properties?.Tags) {
        const tags = entity.properties.Tags.getValue?.() ?? entity.properties.Tags;
        type = tags?.type;
        subtype = tags?.subtype;
      } else if (isLanelet && entity.properties?.relation) {
        const relation = entity.properties.relation.getValue?.() ?? entity.properties.relation;
        type = relation?.type;
        subtype = relation?.subtype;
      }

      // Get style from styleTypes map (priority: type > subtype)
      const styleDef = (type && styleTypes[type]) || (subtype && styleTypes[subtype]);
      if (!styleDef) return;

      // Common color parsing
      const colorOutline = styleDef.defaultOutlineColor ? Color.fromCssColorString(styleDef.defaultOutlineColor) : Color.GRAY;
      const colorInline = styleDef.defaultFillColor ? Color.fromCssColorString(styleDef.defaultFillColor) : Color.GRAY;

      const lineWidth = styleDef.defaultLineWidth ?? 2;
      const style = styleDef.defaultStyle ?? "solid";

      // Apply style for line view
      if (isLine && entity.polyline) {
        if (style === "dashed") {
          entity.polyline.material = new PolylineDashMaterialProperty({
            color: colorOutline,
            dashLength: 16.0,
          });
        } else {
          entity.polyline.material = new ColorMaterialProperty(colorOutline);
        }

        entity.polyline.width = lineWidth;
      }

      // Apply style for lanelet view
      if (isLanelet && entity.polygon) {
        entity.polygon.material = colorInline;
        entity.polygon.outline = true;
        entity.polygon.outlineColor = colorOutline;

        // if (!entity.polygon._originalMaterial) {
        entity.polygon._originalMaterial = entity.polygon.material;
        entity.polygon._originalOutlineColor = entity.polygon.outlineColor;
        // }

        // if (!entity.polygon._secondaryColor) {
        entity.polygon._secondaryColor = colorOutline.withAlpha(0.6);
        // }
      }
    });
  });
};

export const togglePolygonColors = (dataSource, settingSpeedLimit, isToggled) => {
  if (!dataSource || !dataSource.entities) return;

  const entities = dataSource.entities.values;

  entities.forEach((entity) => {
    if (!entity.polygon || !entity.properties || !entity.properties.relation) return;

    const relation = entity.properties.relation.getValue?.() ?? entity.properties.relation;
    if (!relation || typeof relation.speed_limit === "undefined") return;

    const speedRaw = relation.speed_limit;
    const speed = parseInt(speedRaw);
    if (isNaN(speed)) return;

    if (isToggled) {
      const rangesArray = Object.values(settingSpeedLimit);

      const range = rangesArray.find((r) => {
        const parts = r.range.split("-").map(Number);

        if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
          const [min, max] = parts;
          return speed >= min && speed < max;
        } else if (parts.length === 1 && !isNaN(parts[0])) {
          return speed >= parts[0];
        }

        return false;
      });

      if (range) {
        if (!entity.polygon._originalMaterial) {
          entity.polygon._originalMaterial = entity.polygon.material;
          entity.polygon._originalOutlineColor = entity.polygon.outlineColor;
        }

        const color = Color.fromCssColorString(range.value).withAlpha(0.9);
        entity.polygon.material = color;
        entity.polygon.outline = true;
        entity.polygon.outlineColor = color;
        entity.polygon._secondaryColor = color;
      }
    } else {
      if (entity.polygon._originalMaterial) {
        entity.polygon.material = entity.polygon._originalMaterial;
        entity.polygon.outlineColor = entity.polygon._originalOutlineColor || Color.BLACK;

        delete entity.polygon._originalMaterial;
        delete entity.polygon._originalOutlineColor;
        delete entity.polygon._secondaryColor;
      }
    }
  });
};

export const highLightRouteForLaneletViewWithCSV = (laneletIdArray, source2, reset = false) => {
  // Validate inputs
  if (!Array.isArray(laneletIdArray) || !source2?.entities?.values) return;

  const entities = source2.entities.values;
  const now = JulianDate.now();

  // Map: always use string keys to avoid number/string mismatch
  const idToEntity = new Map();

  for (const entity of entities) {

    let id = entity.properties?.id;
    if (typeof id?.getValue === "function") id = id.getValue(now);

    if (id != null) idToEntity.set(String(id).trim(), entity);
  }

  for (const raw of laneletIdArray) {
    const key = String(raw).trim();
    const ent = idToEntity.get(key);
    // console.log(ent);
    if (!ent?.polyline) continue;

    const poly = ent.polyline;

    if (reset) {
      // restore original style if saved
      if (poly._originalMaterial) {
        poly.material = poly._originalMaterial;
        poly.outline = poly._originalOutline ?? false;
        poly.outlineColor = poly._originalOutlineColor ?? Color.BLACK;
        delete poly._originalMaterial;
        delete poly._originalOutline;
        delete poly._originalOutlineColor;
      }
    } else {
      // save once then highlight
      if (!poly._originalMaterial) {
        poly._originalMaterial = poly.material;
        poly._originalOutline = poly.outline;
        poly._originalOutlineColor = poly.outlineColor;
      }
      poly.material = Color.GREEN.withAlpha(0.6);
      poly.outline = true;
      poly.outlineColor = Color.BLACK;
    }
  }
 
};
